/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * The coupon, as the host summary holds it.
 *
 * The frame used to own this: it applied a code and told the host what the
 * total became. That answered "what is the frame charging" and nothing else —
 * the summary could not offer a coupon, only report one, and the buyer had to
 * find the field inside an iframe that looks like part of the page but is not.
 *
 * Now the host asks first. The code is checked before anything is rebuilt, so
 * a bad one says so in place instead of silently producing a session at full
 * price.
 */

/** What the host knows about a coupon it is holding. */
export interface AppliedCoupon {
  code: string;
  /** What comes off, in reais. */
  discount: number;
  /** What is left to pay, in reais. */
  total: number;
  /** Percent off, when the coupon is a percentage; null for a fixed amount. */
  percent: number | null;
}

/** What the validation endpoint answers. */
export interface CouponVerdict {
  valid: boolean;
  code?: string;
  discount_type?: string | null;
  discount_value?: number | null;
  discount_amount_cents?: number;
  final_amount_cents?: number;
  message?: string;
}

/** A code as it is sent and compared: trimmed and upper-cased. */
export const normaliseCode = (code: string): string => (code || '').trim().toUpperCase();

/**
 * The verdict, as the summary holds it — or null when the code was refused.
 *
 * Cents come back from the server and reais are what the summary draws, so
 * the conversion happens once, here, rather than at each place that renders.
 */
export const asApplied = (verdict: CouponVerdict): AppliedCoupon | null => {
  if (!verdict?.valid) return null;
  return {
    code: normaliseCode(verdict.code ?? ''),
    discount: (verdict.discount_amount_cents ?? 0) / 100,
    total: (verdict.final_amount_cents ?? 0) / 100,
    percent: verdict.discount_type === 'PERCENT' ? verdict.discount_value ?? null : null,
  };
};

/**
 * Whether a code may be submitted at all.
 *
 * Payien's own pattern is three to thirty characters of A-Z, digits, hyphen
 * and underscore. Checking it here saves a round trip on a typo and, more to
 * the point, keeps the button from looking broken when nothing was typed.
 */
export const isSubmittable = (code: string): boolean => (
  /^[A-Z0-9_-]{3,30}$/.test(normaliseCode(code))
);

/**
 * The discount as the frame reports it — or null when the figures are empty.
 *
 * Payien is the authority on the number: it revalidated the code and
 * answered `final_amount_cents`, which is what the charge comes out of.
 *
 * Null is for the one answer that cannot be true. A discount of nothing
 * leaving a total of nothing is not a coupon, it is a coupon measured
 * against a price nobody supplied — and taken at face value it drew
 * "A pagar hoje: R$ 0,00" under a plan still priced at R$ 97,75 (BUG-022).
 * Payien refuses that case by name now; this stays as the host's own floor,
 * because the summary must never be the place where an impossible total
 * becomes a visible one. A genuine 100%-off coupon still passes: there the
 * discount is the whole price, not zero.
 *
 * @param reply - What the frame relayed from Payien.
 * @returns The coupon as the summary holds it, or null when it says nothing.
 */
export const fromSession = (
  reply: {
    code: string;
    totalCents: number;
    discountCents: number;
    discountType?: string | null;
    discountValue?: number | null;
  },
): AppliedCoupon | null => {
  if (!(reply.discountCents > 0) && !(reply.totalCents > 0)) return null;
  return {
    code: normaliseCode(reply.code),
    discount: reply.discountCents / 100,
    total: reply.totalCents / 100,
    percent: reply.discountType === 'PERCENT' ? reply.discountValue ?? null : null,
  };
};

/**
 * Payien's refusal codes, in the host's own words.
 *
 * The code crosses the frame boundary, never the sentence. Payien names the
 * reason and the host says it: free text from the frame would land on this
 * page in the frame's voice, its wording, and its language.
 */
const COUPON_ERROR_KEYS: Record<string, string> = {
  coupon_not_found: 'checkoutStep.coupon.errorInvalid',
  coupon_expired: 'checkoutStep.coupon.errorExpired',
  coupon_inactive: 'checkoutStep.coupon.errorInvalid',
  coupon_usage_limit: 'checkoutStep.coupon.errorLimit',
  coupon_minimum_amount: 'checkoutStep.coupon.errorMinimum',
  coupon_billing_frequency: 'checkoutStep.coupon.errorCycle',
  coupon_attempts_exceeded: 'checkoutStep.coupon.errorAttempts',
};

/**
 * The translation key for a refusal.
 *
 * @param errorCode - Payien's code for why it said no.
 * @returns The onboarding key to show, or the generic one.
 */
export const couponErrorKey = (errorCode: string): string => (
  COUPON_ERROR_KEYS[errorCode] ?? 'checkoutStep.coupon.error'
);

/**
 * The fingerprint the session is built from.
 *
 * Only the *fallback* coupon belongs in it. A coupon applied to the open
 * session changes nothing about how that session was built — Payien did the
 * arithmetic inside it — so putting it here would swap the iframe's `src`
 * and throw away the card the buyer had already typed. The fallback coupon
 * does belong: that one reaches the price only by building a new session
 * with the code in it.
 */
export const couponFingerprint = (applied: AppliedCoupon | null): string => (
  applied ? `|coupon:${applied.code}` : ''
);
